/** @jsxImportSource @emotion/react */

import { useState } from "react";
import { css } from "@emotion/react";
import { Tooltip } from "@mui/material";
import BaseButton from "components/common/Button/Base";
import { StoreType, SubType } from "models/business.model";
import { getCustomPrompt } from "pages/remotes";
import { PALETTE } from "styles/base/variables";
import CustomPromptDialog from "components/AiSuggestion/Dialog/CustomPrompt";

const DEFAULT_LOAD_ERROR = "설정을 불러오지 못했습니다.";

function getErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: unknown } } })
    ?.response?.data;
  const msg = data?.message;
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  return fallback;
}

interface AiSuggestionButtonProps {
  storeType: StoreType;
  subType?: SubType;
  bizId: string | undefined;
  startDisabled: boolean;
  startDisabledReason: string;
  isStartLoading: boolean;
  onStart: () => void;
}

function AiSuggestionButton({
  storeType,
  subType,
  bizId,
  startDisabled,
  startDisabledReason,
  isStartLoading,
  onStart
}: AiSuggestionButtonProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInitialContent, setSettingsInitialContent] = useState("");
  const [settingsLoadError, setSettingsLoadError] = useState<string | null>(
    null
  );

  // 설정 팝업은 조회 API 응답이 온 뒤에 띄운다 — 먼저 열고 나서 내용을
  // 채우면 placeholder가 잠깐 보였다 사라지는 깜빡임이 생긴다.
  const openSettings = () => {
    setIsDropdownOpen(false);
    if (!bizId || isSettingsLoading) return;

    setIsSettingsLoading(true);
    getCustomPrompt(storeType, bizId, subType)
      .then((res: any) => {
        setSettingsInitialContent(res?.data?.content || "");
        setSettingsLoadError(null);
      })
      .catch((err: unknown) => {
        setSettingsInitialContent("");
        setSettingsLoadError(getErrorMessage(err, DEFAULT_LOAD_ERROR));
      })
      .finally(() => {
        setIsSettingsLoading(false);
        setIsSettingsOpen(true);
      });
  };

  return (
    <div
      css={css`
        position: relative;
      `}
      onMouseEnter={() => bizId && setIsDropdownOpen(true)}
      onMouseLeave={() => setIsDropdownOpen(false)}
    >
      <BaseButton
        variant="outlined"
        color={PALETTE.primary}
        css={css`
          cursor: default !important;
          white-space: nowrap;
        `}
      >
        {isStartLoading ? "AI 회계처리 중" : "AI 회계처리"}
      </BaseButton>
      <div
        css={css`
          box-sizing: border-box;
          width: 100%;
          padding: 4px 8px;
          display: ${isDropdownOpen ? "flex" : "none"};
          border: 1px solid ${PALETTE.border};
          border-radius: 4px;
          flex-direction: column;
          position: absolute;
          border-top: none;
          top: 100%;
          left: 0;
          background-color: ${PALETTE.light};
          z-index: 100;
        `}
      >
        <BaseButton
          variant="text"
          color={PALETTE.primary}
          disabled={isSettingsLoading}
          onClick={openSettings}
          css={css`
            padding: 8px;
            font-size: 14px;
            white-space: nowrap;
          `}
        >
          {isSettingsLoading ? "불러오는 중" : "설정"}
        </BaseButton>
        <Tooltip title={startDisabledReason}>
          <div>
            <BaseButton
              variant="text"
              color={PALETTE.primary}
              disabled={startDisabled}
              onClick={() => {
                setIsDropdownOpen(false);
                onStart();
              }}
              css={css`
                padding: 8px;
                font-size: 14px;
                white-space: nowrap;
              `}
            >
              회계처리 시작
            </BaseButton>
          </div>
        </Tooltip>
      </div>
      {Boolean(bizId) && (
        <CustomPromptDialog
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          storeType={storeType}
          subType={subType}
          businessId={bizId as string}
          initialContent={settingsInitialContent}
          initialLoadError={settingsLoadError}
        />
      )}
    </div>
  );
}

export default AiSuggestionButton;
